import { WSInfoBox } from "@wingspanhq/fe-component-library";
import {
  IRequirementDefinitionResponse,
  RequirementType
} from "@wingspanhq/payments/dist/interfaces";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import React from "react";

import { SearchRowEngagementStatusRollup } from "@wingspanhq/wingspan-api-sdk";
import { queriesPayeeRows } from "@query";
import { WSQueries } from "../../../../query/WSQuery";
import { useQueryBackgroundChecksSettings } from "../../../../query/backgroundChecks/queries/useQueryBackgroundChecksSettings";
import { useUserId } from "../../../../query/hooks/helpers";
import { useQueryMonitorSettings } from "../../../../query/monitorSettings/query/useQueryMonitorSettings";
import { useQuerySharedFileSettings } from "../../../../query/sharedFile/query/useQuerySharedFileSettings";
import { useXWingspanExpansion } from "../../../../shared/hooks/useXWingspanExpansion";
import { getRequirementDefinitionTemplateByParams } from "../../../RequirementDefinitions/utils/getRequirementDefinitionTemplateByParams";
import { selectorPolicyTypesFromSettings } from "../../modules/InsuranceCoverage/selectors/selectorPolicyTypesFromSettings";

export type AddRequirementInformationProps = {
  requirementDefinition?: IRequirementDefinitionResponse;
  engagementId: string;
};

const USER_IDS_WITH_HIDDEN_PRICE = [
  //https://linear.app/wingspan/issue/PLA-855/fe-hide-idv-requirement-pricing-for-wsol-dbhpuhdhh7dute93vcqxvf
  "DbhpUHdhH7Dute93VCqXvF"
];

export const AddRequirementInformation: React.FC<
  AddRequirementInformationProps
> = ({ engagementId, requirementDefinition }) => {
  const userId = useUserId();

  const xWingspanAccount = useXWingspanExpansion();
  const queryPayeeRowsSummary = queriesPayeeRows.searchPayeeRows.useQuery(
    {
      filter: {
        engagementStatus: {
          noneOf: [SearchRowEngagementStatusRollup.INACTIVE]
        },
        engagements_engagementId: { eq: engagementId }
      },
      page: { size: 1 },
      xWingspanAccount
    },
    {
      select: list => list.pagination?.totalSize ?? 0,
      enabled:
        !!requirementDefinition?.requirementType &&
        [
          RequirementType.BackgroundCheck,
          RequirementType.SharedFile,
          RequirementType.IdentityVerification
        ].includes(requirementDefinition?.requirementType)
    }
  );

  const queryBackgroundChecksSettings = useQueryBackgroundChecksSettings(
    requirementDefinition?.dataSourceId!,
    {
      enabled:
        !!requirementDefinition?.dataSourceId &&
        requirementDefinition?.requirementType ===
          RequirementType.BackgroundCheck
    }
  );

  const querySharedFilesSettings = useQuerySharedFileSettings(
    requirementDefinition?.dataSourceId!,
    {
      enabled:
        !!requirementDefinition?.dataSourceId &&
        requirementDefinition?.requirementType === RequirementType.SharedFile
    }
  );

  const queryMonitorSettings = useQueryMonitorSettings(
    requirementDefinition?.dataSourceId!,
    {
      enabled:
        !!requirementDefinition?.dataSourceId &&
        requirementDefinition?.requirementType ===
          RequirementType.InsuranceCoverage
    }
  );

  const defaultView: React.ReactNode = (
    <WSInfoBox
      p="L"
      title="Requirement added will impact your existing contractors"
    >
      Your existing contractors will be notified of the requirement added to
      this engagement. The requirement will also apply to any future contractors
      added to this engagement.
    </WSInfoBox>
  );

  const views: Record<RequirementType, React.ReactNode> = {
    [RequirementType.BackgroundCheck]: (
      <WSQueries
        queries={{ queryPayeeRowsSummary, queryBackgroundChecksSettings }}
        renderErrors={() => defaultView}
      >
        {({ queryPayeeRowsSummaryData, queryBackgroundChecksSettingsData }) => {
          return (
            <>
              <WSInfoBox
                p="L"
                title={`Requirement added will impact ${queryPayeeRowsSummaryData} existing contractors`}
              >
                {queryPayeeRowsSummaryData} existing contractors will be
                notified of the requirement added to this engagement. The
                requirement will also apply to any future contractors added to
                this engagement.
              </WSInfoBox>

              <WSInfoBox p="L" title="Background check package">
                The selected background check package is the Wingspan Standard
                package and will cost{" "}
                {formatMoney(
                  queryBackgroundChecksSettingsData.package?.costPerCheck || 0,
                  "USD"
                )}{" "}
                per check.
              </WSInfoBox>
            </>
          );
        }}
      </WSQueries>
    ),
    [RequirementType.SharedFile]: (
      <WSQueries
        queries={{ queryPayeeRowsSummary, querySharedFilesSettings }}
        renderErrors={() => defaultView}
      >
        {({ queryPayeeRowsSummaryData, querySharedFilesSettingsData }) => {
          return (
            <>
              <WSInfoBox
                p="L"
                title={`Requirement added will impact ${queryPayeeRowsSummaryData} existing contractors`}
              >
                {queryPayeeRowsSummaryData} existing contractors will be
                notified of the requirement added to this engagement. The
                requirement will also apply to any future contractors added to
                this engagement.
              </WSInfoBox>

              {querySharedFilesSettingsData.cost ? (
                <WSInfoBox p="L" title="Document verification requirement">
                  The selected document verification requirement will cost{" "}
                  {formatMoney(querySharedFilesSettingsData.cost, "USD")} per
                  verification.
                </WSInfoBox>
              ) : null}
            </>
          );
        }}
      </WSQueries>
    ),
    [RequirementType.IdentityVerification]: (
      <WSQueries
        queries={{ queryPayeeRowsSummary }}
        renderErrors={() => defaultView}
      >
        {({ queryPayeeRowsSummaryData }) => {
          const isPriceHidden = USER_IDS_WITH_HIDDEN_PRICE.includes(userId);

          return (
            <>
              <WSInfoBox
                p="L"
                title={`Requirement added will impact ${queryPayeeRowsSummaryData} existing contractors`}
              >
                {queryPayeeRowsSummaryData} existing contractors will be
                notified of the requirement added to this engagement. The
                requirement will also apply to any future contractors added to
                this engagement.
              </WSInfoBox>
              {isPriceHidden ? null : (
                <WSInfoBox p="L" title="Document verification requirement">
                  The selected document verification requirement will cost{" "}
                  {formatMoney(5, "USD")} per verification.
                </WSInfoBox>
              )}
            </>
          );
        }}
      </WSQueries>
    ),
    [RequirementType.Signature]: defaultView,
    [RequirementType.InsuranceCoverage]: (
      <WSQueries
        queries={{ queryPayeeRowsSummary, queryMonitorSettings }}
        renderErrors={() => defaultView}
      >
        {({ queryPayeeRowsSummaryData, queryMonitorSettingsData }) => {
          const template = getRequirementDefinitionTemplateByParams({
            type: RequirementType.InsuranceCoverage,
            policyType: selectorPolicyTypesFromSettings(
              queryMonitorSettingsData
            )
          });

          return (
            <>
              <WSInfoBox
                p="L"
                title={`Requirement added will impact ${queryPayeeRowsSummaryData} existing contractors`}
              >
                {queryPayeeRowsSummaryData} existing contractors will be
                notified of the requirement added to this engagement. The
                requirement will also apply to any future contractors added to
                this engagement.
              </WSInfoBox>

              {template.costPerSubmission ? (
                <WSInfoBox p="L" title="Document verification requirement">
                  The selected document verification requirement will cost{" "}
                  {formatMoney(template.costPerSubmission, "USD")} per
                  verification.
                </WSInfoBox>
              ) : null}
            </>
          );
        }}
      </WSQueries>
    ),
    [RequirementType.Registration]: defaultView,
    [RequirementType.TaxVerification]: defaultView,
    [RequirementType.PayoutMethod]: defaultView
  };

  return (
    <>
      {requirementDefinition?.requirementType
        ? views[requirementDefinition.requirementType]
        : defaultView}
    </>
  );
};
